import { inject, Injectable } from '@angular/core';
import {
  AuthenticationResult,
  createNestablePublicClientApplication,
  IPublicClientApplication,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { Router } from '@angular/router';
import { AppConfigService } from '../config';
import { AuthService } from './auth.service';

interface MicrosoftLoginDialogMessage {
  readonly type: 'microsoftLoginSuccess' | 'microsoftLoginError';
  readonly accessToken?: string;
  readonly error?: string;
}

const MSAL_DIALOG_SESSION_KEY = 'safegpt.msal.dialog';

@Injectable({ providedIn: 'root' })
export class MsalService {
  private readonly appConfigService = inject(AppConfigService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private pca: PublicClientApplication | null = null;
  private nestedPca: IPublicClientApplication | null = null;
  private initialized = false;

  async initialize(): Promise<void> {
    await this.getClient();
  }

  async acquireMicrosoftAccessToken(loginHint?: string): Promise<string> {
    const tokenFromNaa = await this.tryNestedAppAuth(loginHint);
    if (tokenFromNaa) {
      return tokenFromNaa;
    }

    return this.acquireTokenWithOfficeDialog(loginHint);
  }

  async trySilentSso(): Promise<string | null> {
    const client = await this.getClient();
    const account = client.getAllAccounts()[0];
    if (!account) {
      return null;
    }

    client.setActiveAccount(account);
    const response = await client.acquireTokenSilent({
      ...this.createRedirectRequest(account.username),
      account,
    });

    return response.accessToken || null;
  }

  async handleRedirect(): Promise<AuthenticationResult | null> {
    return (await this.getClient()).handleRedirectPromise();
  }

  async startDialogRedirect(loginHint?: string): Promise<void> {
    const client = await this.getClient();
    globalThis.sessionStorage.setItem(MSAL_DIALOG_SESSION_KEY, '1');
    const request = this.createRedirectRequest(loginHint);
    await client.loginRedirect(request);
  }

  async signOut(): Promise<void> {
    const client = await this.getClient();
    await client.clearCache();
  }

  logout(): void {
    this.authService.clearAuthToken();
    void this.router.navigate(['/login'], {
      queryParams: { logout: '1' },
      replaceUrl: true,
    });
  }

  private async getClient(): Promise<PublicClientApplication> {
    if (!this.pca) {
      const msal = this.appConfigService.config().msal;
      this.pca = new PublicClientApplication({
        auth: {
          clientId: msal.clientId,
          authority: msal.authority,
          redirectUri: this.buildRedirectUri(),
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: true,
        },
      });
    }

    if (!this.initialized) {
      await this.pca.initialize();
      this.initialized = true;
    }

    return this.pca;
  }

  private async getNestedClient(): Promise<IPublicClientApplication> {
    if (!this.nestedPca) {
      const msal = this.appConfigService.config().msal;
      this.nestedPca = await createNestablePublicClientApplication({
        auth: {
          clientId: msal.clientId,
          authority: msal.authority,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
        },
      });
    }

    return this.nestedPca;
  }

  private async tryNestedAppAuth(loginHint?: string): Promise<string | null> {
    if (!this.isNestedAppAuthSupported()) {
      return null;
    }

    try {
      const client = await this.getNestedClient();
      const accounts = client.getAllAccounts();
      const request: SilentRequest = {
        scopes: [...this.appConfigService.config().msal.scopes],
        ...(accounts[0] ? { account: accounts[0] } : {}),
        ...(loginHint ? { loginHint } : {}),
      };
      const response = await client.acquireTokenSilent(request);
      return response.accessToken || null;
    } catch (error) {
      console.warn('NAA silent Microsoft login failed; falling back to Office Dialog.', error);
      return null;
    }
  }

  private isNestedAppAuthSupported(): boolean {
    return typeof Office !== 'undefined'
      && !!Office.context?.requirements?.isSetSupported('NestedAppAuth', '1.1');
  }

  private acquireTokenWithOfficeDialog(loginHint?: string): Promise<string> {
    if (typeof Office === 'undefined' || !Office.context?.ui?.displayDialogAsync) {
      throw new Error('Office Dialog API is not available.');
    }

    return new Promise((resolve, reject) => {
      const dialogUrl = this.buildDialogStartUri(loginHint);

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 60, width: 40, displayInIframe: false },
        (result: Office.AsyncResult<Office.Dialog>) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            reject(new Error(result.error?.message || 'Unable to open Microsoft login dialog.'));
            return;
          }

          this.bindMicrosoftLoginDialog(result.value, resolve, reject);
        },
      );
    });
  }

  private bindMicrosoftLoginDialog(
    dialog: Office.Dialog,
    resolve: (accessToken: string) => void,
    reject: (reason: Error) => void,
  ): void {
    let settled = false;
    const settle = (callback: () => void): void => {
      if (settled) {
        return;
      }

      settled = true;
      dialog.close();
      callback();
    };

    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event: unknown) => {
      this.handleDialogMessage(event, resolve, reject, settle);
    });

    dialog.addEventHandler(Office.EventType.DialogEventReceived, (event: { message: string; origin: string | undefined } | { error: number }) => {
      this.handleDialogEvent(event, reject, settle);
    });
  }

  private handleDialogMessage(
    event: unknown,
    resolve: (accessToken: string) => void,
    reject: (reason: Error) => void,
    settle: (callback: () => void) => void,
  ): void {
    const message = this.parseDialogMessage(event);
    if (!message) {
      settle(() => reject(new Error('Microsoft login dialog returned an invalid response.')));
      return;
    }

    const accessToken = message.accessToken;
    if (message.type === 'microsoftLoginSuccess' && accessToken) {
      settle(() => resolve(accessToken));
      return;
    }

    settle(() => reject(new Error(message.error || 'Microsoft login failed.')));
  }

  private handleDialogEvent(
    event: { message: string; origin: string | undefined } | { error: number },
    reject: (reason: Error) => void,
    settle: (callback: () => void) => void,
  ): void {
    const errorCode = 'error' in event ? event.error : 'unknown';
    settle(() => reject(new Error(`Microsoft login dialog closed before authentication completed (${errorCode}).`)));
  }

  private parseDialogMessage(event: unknown): MicrosoftLoginDialogMessage | null {
    const message = typeof event === 'object' && event !== null && 'message' in event
      ? (event as { message?: unknown }).message
      : null;

    if (typeof message !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(message) as Partial<MicrosoftLoginDialogMessage>;
      if (parsed.type === 'microsoftLoginSuccess' || parsed.type === 'microsoftLoginError') {
        return parsed as MicrosoftLoginDialogMessage;
      }
    } catch {
      return null;
    }

    return null;
  }

  private createRedirectRequest(loginHint?: string, redirectUri = this.buildRedirectUri()): RedirectRequest {
    const msal = this.appConfigService.config().msal;

    return {
      scopes: [...msal.scopes],
      redirectUri,
      ...(loginHint ? { loginHint } : {}),
    };
  }

  private buildRedirectUri(): string {
    return `${globalThis.location.origin}${this.appConfigService.config().msal.redirectPath}`;
  }

  private buildDialogStartUri(loginHint?: string): string {
    const url = new URL(this.buildRedirectUri());
    url.searchParams.set('dialog', '1');
    if (loginHint) {
      url.searchParams.set('loginHint', loginHint);
    }

    return url.toString();
  }
}
